import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { resolveLanguagePreference, setStoredLanguage } from '@app/core/i18n/i18n-storage';
import { getActivePortal } from '@app/core/i18n/portal-context';
import {
  DEFAULT_LANGUAGE,
  getLanguageMeta,
  isSupportedLanguage,
  SUPPORTED_LANGUAGES,
  SupportedLanguage,
} from '@app/core/i18n/supported-languages';

/**
 * Owns the active UI locale and the document state that follows from it (lang, dir, body classes).
 *
 * <p>Every portal — store, taxi office and the signed-out screens — honours the same saved
 * preference, which defaults to Arabic.</p>
 */
@Injectable({
  providedIn: 'root',
})
export class I18nService {
  constructor(private readonly translate: TranslateService) {}

  init(): Promise<unknown> {
    this.translate.addLangs(SUPPORTED_LANGUAGES.map((l) => l.code));
    this.translate.setDefaultLang(DEFAULT_LANGUAGE);
    return this.syncWithActivePortal();
  }

  get supportedLanguages(): readonly SupportedLanguage[] {
    return SUPPORTED_LANGUAGES;
  }

  /** Re-apply the saved locale — call after a login/logout changes the active portal. */
  syncWithActivePortal(): Promise<unknown> {
    return this.applyLocale(resolveLanguagePreference());
  }

  setUserLanguage(language: string): void {
    if (!isSupportedLanguage(language) || language === this.getActiveLanguage()) {
      return;
    }
    setStoredLanguage(language);
    void this.applyLocale(language);
  }

  getUserLanguage(): string | null {
    return resolveLanguagePreference();
  }

  getActiveLanguage(): string {
    return this.translate.currentLang || resolveLanguagePreference();
  }

  isRtl(): boolean {
    return getLanguageMeta(this.getActiveLanguage())?.rtl ?? false;
  }

  isPortalRtl(): boolean {
    return this.isRtl();
  }

  getLocale(): string {
    return this.getActiveLanguage() === 'ar' ? 'ar' : 'en';
  }

  setLanguage(language: string): void {
    this.setUserLanguage(language);
  }

  instant(key: string, params?: Record<string, string | number>): string {
    return this.translate.instant(key, params);
  }

  private applyLocale(lang: string): Promise<unknown> {
    const rtl = getLanguageMeta(lang)?.rtl ?? false;
    this.applyDocumentState(lang, rtl);
    return this.translate.use(lang).toPromise();
  }

  private applyDocumentState(lang: string, rtl: boolean): void {
    const portal = getActivePortal();
    document.documentElement.lang = lang;
    document.documentElement.dir = rtl ? 'rtl' : 'ltr';
    document.body.classList.remove('portal-store', 'portal-taxi', 'portal-transit', 'portal-taxi--rtl', 'portal-guest');
    document.body.classList.add(`portal-${portal}`);
    // `.rtl` is what the shared stylesheet mirrors on; keep the taxi-specific hook it already uses.
    document.body.classList.toggle('rtl', rtl);
    // Provider portals (taxi office, bus operator) share the mirrored sidebar chrome.
    if ((portal === 'taxi' || portal === 'transit') && rtl) {
      document.body.classList.add('portal-taxi--rtl');
    }
  }
}
